import {
    ProtocolState,
    ProtocolConfig,
    ProtocolHistoryEntry,
    ChecklistValue,
    GlobalUser
} from '../types';

export interface ApproveProtocolParams {
    currentState: ProtocolState;
    config?: ProtocolConfig | null;
    currentReviewerName: string;
    currentUser?: GlobalUser | null;
    signatureUrl: string;
    checklist: Record<string, ChecklistValue>;
    resolutionText?: string;
    resolutionPhotos?: string[];
    taskName?: string;
    unitName?: string;
}

export interface RejectProtocolParams {
    currentState: ProtocolState;
    currentReviewerName: string;
    currentUser?: GlobalUser | null;
    rejectionReason: string;
    rejectionPhotos?: string[];
    checklist: Record<string, ChecklistValue>;
    taskName?: string;
    protocolName?: string;
}

export interface AdminLiberateParams {
    currentState: ProtocolState;
    currentUser?: GlobalUser | null;
    signatureUrl: string;
    checklist: Record<string, ChecklistValue>;
    taskName?: string;
    protocolName?: string;
}

export interface ResolveProtocolParams {
    currentState: ProtocolState;
    currentReviewerName: string;
    currentUser?: GlobalUser | null;
    resolutionText: string;
    resolutionPhotos?: string[];
    checklist: Record<string, ChecklistValue>;
    taskName?: string;
    protocolName?: string;
}

export interface ReopenProtocolParams {
    currentState: ProtocolState;
    currentUser?: GlobalUser | null;
    reason: string;
}

/**
 * Aprueba el protocolo para el revisor actual y lo avanza al siguiente revisor o lo cierra definitivamente.
 * Centraliza la regla de negocio para web y móvil asegurando sello updatedAt e historial consistente.
 */
export const approveProtocol = (params: ApproveProtocolParams): ProtocolState => {
    const {
        currentState,
        config,
        currentReviewerName,
        currentUser,
        signatureUrl,
        checklist,
        resolutionText,
        resolutionPhotos = [],
        taskName,
        unitName
    } = params;

    const group = config?.reviewerGroups?.find(g => g.unitIds.includes(currentState.unitId));
    const totalReviewers = group?.reviewers.length || 1;
    const isLast = currentState.currentReviewerIndex >= totalReviewers - 1;
    const nextStatus = isLast ? 'Cerrado' : (currentState.status === 'Rechazado' ? 'Resuelto' : 'Abierto');
    const nextReviewerIndex = isLast ? currentState.currentReviewerIndex : currentState.currentReviewerIndex + 1;

    const updatedSignatures = { ...currentState.signatures };
    if (signatureUrl) {
        updatedSignatures[currentState.currentReviewerIndex] = signatureUrl;
    }

    const history: ProtocolHistoryEntry[] = [...currentState.history];

    // Si venía de rechazo, registrar primero la resolución
    if (currentState.status === 'Rechazado') {
        history.push({
            date: new Date().toLocaleString(),
            action: 'resolved',
            user: currentUser?.name || currentReviewerName,
            comment: `Resolución: ${resolutionText || 'Observaciones subsanadas'}`,
            images: resolutionPhotos,
            snapshot: JSON.parse(JSON.stringify(checklist))
        });
    }

    // Registrar la aprobación de la etapa
    history.push({
        date: new Date().toLocaleString(),
        action: isLast ? 'closed' : 'approved',
        user: currentUser?.name || currentReviewerName,
        comment: isLast
            ? `Protocolo aprobado y cerrado en su totalidad por ${currentReviewerName}.`
            : `Aprobado por ${currentReviewerName}. Pasa a revisión ${nextReviewerIndex + 1}.`,
        snapshot: JSON.parse(JSON.stringify(checklist))
    });

    return {
        ...currentState,
        status: nextStatus,
        currentReviewerIndex: nextReviewerIndex,
        checklist,
        signatures: updatedSignatures,
        taskName: taskName || currentState.taskName,
        protocolName: config?.protocolName || currentState.protocolName,
        history,
        updatedAt: new Date().toISOString()
    };
};

/**
 * Rechaza el protocolo enviándolo al revisor inmediatamente anterior con motivo y fotografías de respaldo.
 */
export const rejectProtocol = (params: RejectProtocolParams): ProtocolState => {
    const {
        currentState,
        currentReviewerName,
        currentUser,
        rejectionReason,
        rejectionPhotos = [],
        checklist,
        taskName,
        protocolName
    } = params;

    const prevReviewerIndex = Math.max(0, currentState.currentReviewerIndex - 1);
    const historyEntry: ProtocolHistoryEntry = {
        date: new Date().toLocaleString(),
        action: 'rejected',
        user: currentUser?.name || currentReviewerName,
        comment: `Rechazado por ${currentReviewerName}. Obs: ${rejectionReason.trim()}`,
        snapshot: JSON.parse(JSON.stringify(checklist)),
        images: rejectionPhotos
    };

    return {
        ...currentState,
        status: 'Rechazado',
        currentReviewerIndex: prevReviewerIndex,
        checklist,
        taskName: taskName || currentState.taskName,
        protocolName: protocolName || currentState.protocolName,
        history: [...currentState.history, historyEntry],
        updatedAt: new Date().toISOString()
    };
};

/**
 * Libera un protocolo directamente como Administrador del Proyecto, cerrándolo inmediatamente.
 */
export const adminLiberateProtocol = (params: AdminLiberateParams): ProtocolState => {
    const {
        currentState,
        currentUser,
        signatureUrl,
        checklist,
        taskName,
        protocolName
    } = params;

    const updatedSignatures = { ...currentState.signatures };
    if (signatureUrl) {
        updatedSignatures[currentState.currentReviewerIndex] = signatureUrl;
    }

    const adminName = currentUser?.name || 'Administrador';
    const historyEntry: ProtocolHistoryEntry = {
        date: new Date().toLocaleString(),
        action: 'closed',
        user: `Admin: ${adminName}`,
        comment: `Protocolo liberado con atribución de Administrador por ${adminName}.`,
        snapshot: JSON.parse(JSON.stringify(checklist))
    };

    return {
        ...currentState,
        status: 'Cerrado',
        checklist,
        signatures: updatedSignatures,
        taskName: taskName || currentState.taskName,
        protocolName: protocolName || currentState.protocolName,
        history: [...currentState.history, historyEntry],
        updatedAt: new Date().toISOString()
    };
};

/**
 * Reabre un protocolo cerrado o rechazado retornándolo a estado Abierto en el primer revisor.
 */
export const reopenProtocol = (params: ReopenProtocolParams): ProtocolState => {
    const { currentState, currentUser, reason } = params;
    const userName = currentUser?.name || 'Usuario';

    const historyEntry: ProtocolHistoryEntry = {
        date: new Date().toLocaleString(),
        action: 'created',
        user: userName,
        comment: `Protocolo reabierto por ${userName}. Motivo: ${reason}`
    };

    return {
        ...currentState,
        status: 'Abierto',
        currentReviewerIndex: 0,
        history: [...currentState.history, historyEntry],
        updatedAt: new Date().toISOString()
    };
};

/**
 * Resuelve las observaciones de un protocolo rechazado, registrando la evidencia y cambiando el estado a Resuelto.
 */
export const resolveProtocol = (params: ResolveProtocolParams): ProtocolState => {
    const {
        currentState,
        currentReviewerName,
        currentUser,
        resolutionText,
        resolutionPhotos = [],
        checklist,
        taskName,
        protocolName
    } = params;

    const historyEntry: ProtocolHistoryEntry = {
        date: new Date().toLocaleString(),
        action: 'resolved',
        user: currentUser?.name || currentReviewerName,
        comment: `Resolución: ${resolutionText || 'Observaciones subsanadas'}`,
        images: resolutionPhotos,
        snapshot: JSON.parse(JSON.stringify(checklist))
    };

    return {
        ...currentState,
        status: 'Resuelto',
        checklist,
        taskName: taskName || currentState.taskName,
        protocolName: protocolName || currentState.protocolName,
        history: [...currentState.history, historyEntry],
        updatedAt: new Date().toISOString()
    };
};

/**
 * Extrae el conjunto de URLs de fotografías heredadas de etapas de revisión anteriores ya aprobadas.
 */
export const getBaseProtocolPhotos = (protocolState?: ProtocolState | null): Set<string> => {
    const basePhotos = new Set<string>();
    if (!protocolState || !Array.isArray(protocolState.history)) return basePhotos;

    const approvedEntries = protocolState.history.filter(h => h && (h.action === 'approved' || h.action === 'closed'));
    if (approvedEntries.length > 0) {
        const lastApproved = approvedEntries[approvedEntries.length - 1];
        if (lastApproved && lastApproved.snapshot) {
            Object.values(lastApproved.snapshot).forEach(it => {
                if (Array.isArray(it.photos)) {
                    it.photos.forEach(ph => {
                        if (ph && typeof ph === 'string') {
                            basePhotos.add(ph.trim());
                        }
                    });
                }
            });
        }
    }
    return basePhotos;
};

/**
 * Valida si el revisor actual ha adjuntado al menos una fotografía propia/nueva en su etapa de revisión.
 */
export const hasReviewerAddedPhoto = (
    tempChecklist: Record<string, { photos?: string[] }> | undefined | null,
    currentState?: ProtocolState | null
): boolean => {
    if (!tempChecklist) return false;
    const basePhotos = getBaseProtocolPhotos(currentState);

    for (const item of Object.values(tempChecklist)) {
        if (Array.isArray(item.photos)) {
            for (const p of item.photos) {
                if (typeof p === 'string' && p.trim() !== '' && !basePhotos.has(p.trim())) {
                    return true;
                }
            }
        }
    }
    return false;
};
